import type { ExistingEquipmentPositionSlot } from 'utils/existing-equipment';
import { isPackagedSystemTypeLabel } from 'utils/system-type-label';

export type OpenSlotPlaceholderLabels = {
  keptOutdoorLabel?: string;
  keptIndoorLabel?: string;
  keptBlowerLabel?: string;
  keptBlowerHeatingCapacity?: number | null;
  requiredOutdoorLabel?: string;
  requiredIndoorLabel?: string;
  requiredBlowerLabel?: string;
  // A packaged system type, named ("Packaged Gas"): its open indoor and
  // blower slots are neither kept nor required — the coil and blower are
  // built into the unit the outdoor slot holds.
  packagedUnitLabel?: string;
};

export type KeptBlower = {
  label: string;
  heatingCapacityBtu: number | null;
};

type OpenSlotRole = 'kept' | 'required' | null;

type ResolveOpenSlotPlaceholderLabelsParams = {
  combo: Record<ExistingEquipmentPositionSlot, unknown>;
  systemTypeLabel: string;
  keptSlots: ReadonlySet<ExistingEquipmentPositionSlot>;
  keptBlower: KeptBlower | null;
};

export function systemTypeLabelParts(systemTypeLabel: string): string[] {
  return systemTypeLabel.split('+').map((part) => part.trim());
}

function openSlotRole(product: unknown, kept: boolean): OpenSlotRole {
  if (product) return null;
  return kept ? 'kept' : 'required';
}

export function resolveOpenSlotPlaceholderLabels({
  combo,
  systemTypeLabel,
  keptSlots,
  keptBlower
}: ResolveOpenSlotPlaceholderLabelsParams): OpenSlotPlaceholderLabels {
  const [outdoorLabel, indoorLabel, blowerLabel] =
    systemTypeLabelParts(systemTypeLabel);
  const outdoor = openSlotRole(combo.outdoor, keptSlots.has('outdoor'));
  const outdoorLabels = {
    keptOutdoorLabel: outdoor === 'kept' ? outdoorLabel : undefined,
    requiredOutdoorLabel: outdoor === 'required' ? outdoorLabel : undefined
  };
  // Nothing after the "+" on a packaged label is a component: a kept blower
  // position or an empty indoor slot says nothing about a unit that has
  // both built in.
  if (isPackagedSystemTypeLabel(systemTypeLabel)) {
    return { ...outdoorLabels, packagedUnitLabel: outdoorLabel };
  }
  const indoor = openSlotRole(combo.indoor, keptSlots.has('indoor'));
  const blower = openSlotRole(combo.blower, keptBlower != null);
  return {
    ...outdoorLabels,
    keptIndoorLabel: indoor === 'kept' ? indoorLabel : undefined,
    requiredIndoorLabel: indoor === 'required' ? indoorLabel : undefined,
    keptBlowerLabel:
      blower === 'kept' && keptBlower
        ? blowerLabel ?? keptBlower.label
        : undefined,
    keptBlowerHeatingCapacity:
      blower === 'kept' && keptBlower
        ? keptBlower.heatingCapacityBtu
        : undefined,
    requiredBlowerLabel: blower === 'required' ? blowerLabel : undefined
  };
}
